// src/apiurl.js
//
// API routing strategy:
//
// The app is served at multiple origins:
//   1. https://passv2-portal-linux-eastus-as.azurewebsites.net   (Azure direct / prod)
//   2. https://passv2-portal-linux-eastus-as-production-stage.azurewebsites.net (stage slot)
//   3. https://pass.cdc.gov                                        (CDC public reverse proxy)
//   4. http://localhost:3000 / http://localhost:5001               (local dev)
//
// API base URL priority:
//   1. REACT_APP_API_BASE_URL env var (set only for local dev where API port differs)
//   2. window.location.origin at runtime — covers all production origins dynamically
//
// This means the build is origin-agnostic: the same JS bundle works on Azure,
// the CDC proxy, and any future host without rebuilding.
//
// For local dev: set REACT_APP_API_BASE_URL=http://localhost:5001 in src/.env
// (the React app runs on port 3000, the API on 5001).

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const getEnvValue = (...keys) => {
  for (const key of keys) {
    const value = process.env[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.replace(/^['"]|['"]$/g, "").trim();
    }
  }
  return undefined;
};

// ─────────────────────────────────────────────────────────────────────────────
//  AZURE AD / MSAL
// ─────────────────────────────────────────────────────────────────────────────

export const clientId = getEnvValue(
  "REACT_APP_AZURE_KEY_CLIENTID",
  "REACT_APP_AZURE_KEY_CLIENT_ID",
  "REACT_APP_AZURE_CLIENT_ID",
  "AZURE_CLIENT_ID"
);

const tenantId = getEnvValue(
  "REACT_APP_AZURE_KEY_TENANT_ID",
  "REACT_APP_AZURE_TENANT_ID"
);

export const authority =
  getEnvValue("REACT_APP_AZURE_KEY_AUTHORITY", "REACT_APP_AZURE_AUTHORITY") ||
  (tenantId
    ? `https://login.microsoftonline.com/${tenantId}`
    : "https://login.microsoftonline.com/common");

// redirectUri uses the runtime origin so MSAL redirects back to whichever
// host the user actually loaded — pass.cdc.gov, Azure direct, or stage.
export const redirectUri =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : undefined;

// ─────────────────────────────────────────────────────────────────────────────
//  API BASE URL  (resolved at runtime — never baked in at build time)
//
//  Priority:
//    1. REACT_APP_API_BASE_URL  — only needed for local dev (API on a different port)
//    2. window.location.origin  — the correct host for every production deployment
//
//  Result: the same JS bundle works on pass.cdc.gov, the Azure App Service, the
//  staging slot, and any future host without rebuilding or CI env-var changes.
// ─────────────────────────────────────────────────────────────────────────────

function resolveApiPath() {
  // Local dev override (e.g. REACT_APP_API_BASE_URL=http://localhost:5001)
  const envUrl = getEnvValue("REACT_APP_API_BASE_URL");
  if (envUrl) return envUrl.replace(/\/$/, "");

  // Dynamic runtime origin — works for every production host automatically
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "";
}

export const apiPath = resolveApiPath();

// ─────────────────────────────────────────────────────────────────────────────
//  OTHER EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

export const requiredGroupId = getEnvValue(
  "REACT_APP_AZURE_ADMIN_GROUP_ID_USER",
  "REACT_APP_AZURE_REQUIRED_GROUP_ID",
  "AZURE_ADMIN_GROUP_ID_USER",
  "AZURE_REQUIRED_GROUP_ID"
);

// Members of this group get the 'admin' role and bypass the user-group check.
export const adminGroupId = getEnvValue(
  "REACT_APP_AZURE_ADMIN_GROUP_ID_ADMIN",
  "REACT_APP_AZURE_ADMIN_GROUP_ID",
  "AZURE_ADMIN_GROUP_ID_ADMIN",
  "AZURE_ADMIN_GROUP_ID"
);

// The full scope URI for the backend API (from "Expose an API" in Azure AD).
// When set, the frontend acquires a separate access token for this scope and
// sends it as the Authorization header.  The server validates it against
// AZURE_API_APP_ID.  Leave unset to use the legacy ID-token pattern.
// Example: api://45bef5cc-da89-4313-86fd-cd57a377e8e2/access
export const apiScope = getEnvValue(
  "REACT_APP_AZURE_API_SCOPE",
  "REACT_APP_AZURE_KEY_API_SCOPE"
);

// Graph API scopes — always needed for profile and group-membership checks.
export const graphScopes = ["User.Read", "GroupMember.Read.All"];

export const scopes = (() => {
  const raw = getEnvValue(
    "REACT_APP_AZURE_KEY_SCOPES",
    "REACT_APP_AZURE_SCOPES"
  );
  if (raw) {
    const parsed = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (parsed.length > 0) return parsed;
  }
  return ["openid", "profile", "email", "User.Read", "GroupMember.Read.All"];
})();

export const sraUrl = "https://www.ncbi.nlm.nih.gov/sra/?term=";

export const azureBlobArtifactBaseUrl = getEnvValue(
  "AZURE_BLOB_ARTIFACT_BASE_URL",
  "REACT_APP_AZURE_KEY_DATABASE_URL"
);